
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { computed, defineComponent, nextTick, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import LoadMoreButton from '@/components/load-more-button.vue'
import ContentAdaptLayout from '../../_components/content-adapt-layout/content-adapt-layout.vue'

import {
  AdminOperationLogService,
  FetchOptions,
  isAdminLogDetailDisabled,
  LogItem,
  SECOND_MODEL_PATH
} from './admin-operation-log.service'
import { models as STATIC_ADMIN_MODEL_TREE } from './model_names.js'
import type { AdminLogModelNode } from './model_names'

import SwitchTerminalGroup from './_components/switch-terminal-group/switch-terminal-group.vue'
import MessagePush from './_components/message-push/message-push.vue'
import { useRoute, useRouter } from 'vue-router'
import GroupPolicyDrawer from '@/views/dashboard/_components/group-policy-settings/group-policy-drawer.vue'
import Approval from './_components/Approval.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { FormType, ObjectFormItem, DateType } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
import dayjs from 'dayjs'
import { adminOperationDisplayText } from '@/utils/admin-operation-display'


const MESSAGE_FIRST_MODEL_ID = 3
const POLICY_SETTINGS_FIRST_MODEL_ID = 5
const MESSAGE_Internal_LIMIT_MODEL_ID = 8
const SWITCH_TERMINAL_SECOND_MODEL_ID = 8
const SWITCH_OUT_DATA_SECOND_MODEL_ID = 9

export default defineComponent({
  components: {
    ExportDropdown,
    SearchFilterBar,
    LoadMoreButton,
    GroupPolicyDrawer,
    MessagePush,
    SwitchTerminalGroup,
    ContentAdaptLayout,Approval,
    RawDataDrawer
  },
  setup () {
    const { t, te, locale } = useI18n()
    const router = useRouter()
    const route = useRoute()
    const loading = ref(true)
    const injector = useReflectiveInjector([AdminOperationLogService])
    const adminOperationLogService = injector.get(AdminOperationLogService)

    const end = new Date()
    const start = new Date()
    // eslint-disable-next-line no-magic-numbers
    end.setHours(23, 59, 59, 999)
    // eslint-disable-next-line no-magic-numbers
    start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)

    const hasMore = ref(true)
    const defaultDate = [dayjs(start).format('YYYY-MM-DD'), dayjs(end).format('YYYY-MM-DD')]
    const [start_time, end_time] = useParseTime(defaultDate)

    const dialogDetailVisible = ref<boolean>(false)
    const dialogDetailOptions = reactive({
      record_id: null,
      first_model_id: null,
      second_model_id: null,
      opt_data: {}
    })
    const groupPolicyDrawerData = reactive({
      visible: false,
      policyId: 0
    })

    const queryParams = reactive<any>({
      key_word: '',
      start_time: start_time,
      end_time: end_time,
      first_model_id: null,
      second_model_id: null,
      page: 1,
      limit: 20,
      dateRange: defaultDate
    })

    function handleQueryChange () {
      queryParams.page = 1
      fetchData()
    }

    const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
      void locale.value
      return {
        dateRange: {
          colOpt: { style: 'flex: 0 0 280px' },
          label: t('noticeManage.filterDateRange'),
          type: FormType.Date,
          dateType: DateType.DateRange,
          events: {
            change: (val: any) => {
                const [start, end] = useParseTime(val)
                queryParams.start_time = start
                queryParams.end_time = end
                handleQueryChange()
            },
          },
          dateOpt: {
            'value-format': 'YYYY-MM-DD',
            'start-placeholder': t('common.startDate'),
            'end-placeholder': t('common.endDate'),
            clearable: false,
          },
        },
        first_model_id: {
          colOpt: { style: 'flex: 0 0 160px' },
          slot: true,
          label: t('noticeManage.firstModule'),
          hiddenAllLabel: true,
          outSearchFiledFlag: true,
          events: {
            change: handleQueryChange,
          },
        },
        second_model_id: {
          colOpt: { style: 'flex: 0 0 160px' },
          slot: true,
          label: t('noticeManage.secondModule'),
          hiddenAllLabel: true,
          outSearchFiledFlag: true,
          events: {
            change: handleQueryChange,
          },
        },
      }
    })

    const data = reactive({
      total: 0,
      list: []
    })
    const isShowRawData = ref(false)
    const currentRow = ref<any>({})

    function fetchData (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        data.list = []
      }

      loading.value = true


      adminOperationLogService.fetchAdminOperationLogData(queryParams).then(response => {
        if (queryParams.page === 1) {
          data.list = response.list || []
        } else {
          data.list = [...data.list, ...(response.list || [])]
        }
        data.total = response.total || 0
        hasMore.value = data.list.length < data.total
      }).finally(() => {
        loading.value = false
      })
    }

    function handleQuery (form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      fetchData()
    }

    function handleReset () {
      queryParams.key_word = ''
      queryParams.first_model_id = null
      queryParams.second_model_id = null
      queryParams.dateRange = defaultDate
      const [start, end] = useParseTime(defaultDate)
      queryParams.start_time = start
      queryParams.end_time = end
      queryParams.page = 1
      modelData.secondModelSelectionOptions = allSecondModuleOptions
      fetchData()
    }

    function loadMoreData () {
      if (!hasMore.value || loading.value) return
      queryParams.page = queryParams.page! + 1
      fetchData(true)
    }

    function flattenSecondModules (tree: AdminLogModelNode[]) {
      const out: AdminLogModelNode[] = []
      for (const m of tree) {
        for (const s of m.models) {
          out.push(s)
        }
      }
      return out
    }

    function buildIdLabelMaps (tree: AdminLogModelNode[]) {
      const firstModelIdMap: Record<string, string> = {}
      const secondModelIdMap: Record<string, string> = {}
      for (const m of tree) {
        firstModelIdMap[m.model_id] = m.labelKey
        firstModelIdMap[String(m.model_id)] = m.labelKey
        for (const s of m.models) {
          secondModelIdMap[s.model_id] = s.labelKey
          secondModelIdMap[String(s.model_id)] = s.labelKey
        }
      }
      return { firstModelIdMap, secondModelIdMap }
    }

    const allSecondModuleOptions = flattenSecondModules(STATIC_ADMIN_MODEL_TREE)
    const { firstModelIdMap: staticFirstMap, secondModelIdMap: staticSecondMap } = buildIdLabelMaps(STATIC_ADMIN_MODEL_TREE)

    const modelData = reactive({
      models: STATIC_ADMIN_MODEL_TREE,
      secondModels: allSecondModuleOptions,
      secondModelSelectionOptions: allSecondModuleOptions,
      firstModelIdMap: staticFirstMap,
      secondModelIdMap: staticSecondMap
    })

    function tModuleLabel (map: Record<string, string>, idRaw: string | number | null | undefined) {
      if (idRaw === null || idRaw === undefined || idRaw === '') return '--'
      const id = Number(idRaw)
      const key =
        map[idRaw as string] ??
        map[String(id)] ??
        (Number.isFinite(id) ? map[id] : undefined)
      if (!key) return t('noticeManage.adminModuleFallback', { id: idRaw })
      return t(key)
    }

    function formatAdminOperation (row: LogItem) {
      void locale.value
      return adminOperationDisplayText(t, te, row)
    }

    function showGroupPolicy (secondModelId: number, data: any) {
      groupPolicyDrawerData.visible = true
      groupPolicyDrawerData.policyId = data.id || 0
    }

    fetchData()

    return {
      tModuleLabel,
      formatAdminOperation,
      loading,
      MESSAGE_FIRST_MODEL_ID,
      isShowRawData,
      currentRow,
      showRawData (row: any) {
        currentRow.value = row
        isShowRawData.value = true
      },
      SWITCH_TERMINAL_SECOND_MODEL_ID,
      isAdminLogDetailDisabled,
      dialogDetailVisible,
      groupPolicyDrawerData,
      dialogDetailOptions,
      queryParams,
      filterFormColumns,
      hasMore,
      data,
      modelData,
      download (type: 0|1) {
        const params: FetchOptions = {
          key_word: queryParams.key_word,
          start_time: queryParams.start_time,
          end_time: queryParams.end_time,
          first_model_id: (queryParams.first_model_id || queryParams.first_model_id === 0) ? queryParams.first_model_id : null,
          second_model_id: (queryParams.second_model_id || queryParams.second_model_id === 0) ? queryParams.second_model_id : null,
          page: 0,
          limit: 0
        }
        adminOperationLogService.download(type, params, t('noticeManage.exportFileAdminOperationLog'))
      },
      async handleFirstModelChange (id: number) {
        // 变更二级模块数据（与 model_names.js 静态配置一致）
        if (typeof id === 'number') {
          const model = modelData.models.find((m: AdminLogModelNode) => m.model_id === id)
          modelData.secondModelSelectionOptions = model ? model.models : allSecondModuleOptions
        } else {
          modelData.secondModelSelectionOptions = allSecondModuleOptions
        }

        await nextTick()

        if (typeof id === 'number' && modelData.secondModelSelectionOptions.findIndex((m: AdminLogModelNode) => {
          return m.model_id === queryParams.second_model_id
        }) === -1) {
          queryParams.second_model_id = null
        }

        queryParams.page = 1
        fetchData()
      },
      loadMoreData,
      handleQuery,
      handleQueryChange,
      handleReset,
      handlerViewDetail ($event: MouseEvent) {
        const { id, first, second, opt } = ($event.currentTarget as HTMLElement).dataset
        const recordId = Number(id)
        const firstModelId = Number(first)
        const secondModelId = Number(second)
        console.log('secondModelId :>> ', secondModelId);
        let optData = null
        try {
          optData = JSON.parse(opt)
          if (secondModelId === 23) {
            optData.message = optData.file_name
          }
          if (secondModelId === 22) {
            optData.message = optData.soft_name_list.join(',')
          }
        } catch (error) {
          // DO NOTHING
        }
        console.log('second :>> ', second);
        if (SECOND_MODEL_PATH[secondModelId]) {
          router.push(SECOND_MODEL_PATH[secondModelId])
          return
        }
        if (firstModelId === POLICY_SETTINGS_FIRST_MODEL_ID) {
          showGroupPolicy(secondModelId, optData)
          return
        }

        Object.assign(dialogDetailOptions, {
          record_id: recordId,
          first_model_id: firstModelId,
          second_model_id: secondModelId,
          opt_data: optData
        })
        dialogDetailVisible.value = true
      }
    }
  }
})
